import { useEffect, useState } from 'react';
import { Col, Form, Input, Row, Select, message, Breadcrumb, DatePicker, Divider } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { SaveOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import TextArea from 'antd/es/input/TextArea';
import Box from '../../../components/Box';
import Button from '../../../components/Button';
import { PageStyled } from '../../styles';
import FormItemNoMarginStyled from '../../../components/FormItem/FormItemNoMarginStyled';
import { setLoading } from '../../../store/loading/actions';
import { setShopState } from '../../../store/shop/actions';
import couponApis from '../../../services/couponApis';
import accountApis from '../../../services/accountApis';
import Text from '../../../components/Text';
import { templateTypeOptions, templateTypeValues } from './services/constant';
import ActivePrintSwitch from './components/ActivePrintSwitch';
import { IResponseDataBody } from '../../../utils/response';
import { ICouponDetail } from './services/types';
import { ICreateOrUpdateCouponBody } from '../../../services/apisType';
import SelectExpiresDate from './components/SelectExpiresDate';
import TemplateImageUpload from './components/TemplateImageUpload';

export interface ITemplateItem {
  id: number | string;
  urlImage: string;
  templateType: string;
}

type TFormUpdate = {
  templateType: string;
  activePrint: boolean;
  couponName: string;
  subTitle: string;
  description: string;
  promotionValue: number;
  discountType: 'MONEY' | 'PERCENT';
  couponQuantity: number;
  startDate: dayjs.Dayjs;
  expiresDate: string;
  urlImage: string;
};

const CouponPage = () => {
  const { shopId, id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { Option } = Select;
  const [formUpdate] = Form.useForm<TFormUpdate>();
  const [couponDetail, setCouponDetail] = useState<ICouponDetail | null>(null);
  const [shopDate, setShopDate] = useState<any>(null);
  const [lstCouponTemplate, setLstCouponTemplate] = useState<ITemplateItem[]>([]);

  const fetchData = async () => {
    dispatch(setLoading(true));
    try {
      if (id) {
        const responDetail: IResponseDataBody<ICouponDetail> = await couponApis.getCouponDetail(id);
        if (responDetail.data.data) {
          const data = responDetail.data.data;
          setCouponDetail(data);
          const detailsTemplate: ITemplateItem = {
            id: 4,
            urlImage: data.urlImage,
            templateType: data.templateType,
          };
          const listCouponTemplate: ITemplateItem[] = [detailsTemplate];
          setLstCouponTemplate(listCouponTemplate);
          const expiresDate = dayjs(data.endTime).format('MM-DD-YYYY');
          formUpdate.setFieldsValue({
            templateType: data.templateType,
            activePrint: data.activePrint,
            couponName: data.couponName,
            subTitle: data.title,
            description: data.description,
            promotionValue: data.discountValue,
            discountType: data.discountType,
            couponQuantity: data.quantity,
            startDate: dayjs(data.startTime),
            expiresDate: expiresDate,
            urlImage: data.urlImage,
          });
        } else {
          message.error({ content: responDetail.data.message, duration: 2 });
        }

        const resShopById = await accountApis.getShopById(shopId);
        if (resShopById.status === 200) {
          setShopDate(resShopById.data.data);
          dispatch(setShopState(resShopById.data.data));
        }
      }
    } catch (error: any) {
      message.error(error.message);
    } finally {
      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    if (id && shopId) {
      fetchData();
    } else {
      formUpdate.setFieldsValue({
        expiresDate: dayjs().format('MM-DD-YYYY'),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, shopId]);

  const selectAfter = (
    <Form.Item name='discountType' style={{ marginBottom: 0, minWidth: 100 }}>
      <Select>
        <Option value='MONEY'>Dollar</Option>
        <Option value='PERCENT'>Percent</Option>
      </Select>
    </Form.Item>
  );

  const onSubmit = async (values: TFormUpdate) => {
    try {
      dispatch(setLoading(true));
      const body: ICreateOrUpdateCouponBody = {
        ...(couponDetail ? { couponId: couponDetail.id } : {}),
        shopId: shopId || '',
        templateType: values.templateType,
        activePrint: values.activePrint,
        couponName: values.couponName,
        title: values.subTitle,
        description: values.description,
        discountValue: values.promotionValue,
        discountType: values.discountType,
        quantity: values.couponQuantity,
        startTime: dayjs(values.startDate).format('MM-DD-YYYY'),
        endTime: values.expiresDate,
        urlImage: values.urlImage,
      };
      const response = id ? await couponApis.updateV2(body) : await couponApis.createV2(body);
      if (response.status === 200) {
        message.success(id ? 'Update successful' : 'Create successful');
        navigate(`/coupons/${shopId}`);
      } else {
        message.error({ content: response.data.message, duration: 2 });
      }
    } catch (error: any) {
      message.error({ content: error.message as string, duration: 1 });
    } finally {
      dispatch(setLoading(false));
    }
  };

  return (
    <PageStyled>
      <Form
        form={formUpdate}
        layout='vertical'
        onFinish={onSubmit}
        initialValues={{
          startDate: dayjs(),
          templateType: templateTypeValues.TICKET,
          discountType: 'MONEY',
          activePrint: false,
        }}
      >
        <Box className='d-between'>
          <Breadcrumb
            style={{ marginBottom: '10px' }}
            items={[{ title: 'Salon ' + shopDate?.shopName }, { title: id ? 'Update Counpon' : 'Create Counpon' }]}
            itemRender={(route) => {
              return <BreadcrumbItemStyled>{route.title}</BreadcrumbItemStyled>;
            }}
          />
          <Box display='flex' gap={1}>
            <Button onClick={() => navigate(`/coupons/${shopId}`)}>Cancel</Button>
            <Button variant='PRIMARY' onClick={() => formUpdate.submit()}>
              <SaveOutlined style={{ marginRight: '5px' }} />
              Save
            </Button>
          </Box>
        </Box>

        <CouponsContentStyled>
          <Row>
            <Col xs={{ span: 24 }} md={{ span: 16 }} lg={{ span: 16 }} xl={{ span: 16 }}>
              <Box style={{ background: 'white', borderRadius: '5px', textAlign: 'left' }} p={1.5}>
                <Row>
                  <Col xs={{ span: 24 }} md={{ span: 12 }} lg={{ span: 12 }} xl={{ span: 12 }}>
                    <FormItemNoMarginStyled label='Type' name='templateType'>
                      <Select size='large' options={templateTypeOptions} />
                    </FormItemNoMarginStyled>
                  </Col>
                  <Col xs={{ span: 24 }} md={{ span: 12 }} lg={{ span: 12 }} xl={{ span: 12 }}>
                    <FormItemNoMarginStyled label='   ' name='activePrint'>
                      <ActivePrintSwitch />
                    </FormItemNoMarginStyled>
                  </Col>
                  <Col xs={{ span: 24 }} md={{ span: 12 }} lg={{ span: 12 }} xl={{ span: 12 }}>
                    <FormItemNoMarginStyled
                      name='couponName'
                      label='Title'
                      rules={[
                        {
                          required: true,
                          message: 'Title is required',
                        },
                        {
                          max: 50,
                          message: 'Max: 50 characters',
                        },
                      ]}
                    >
                      <Input size='large' placeholder='Please enter..' />
                    </FormItemNoMarginStyled>
                  </Col>
                  <Col xs={{ span: 24 }} md={{ span: 12 }} lg={{ span: 12 }} xl={{ span: 12 }}>
                    <FormItemNoMarginStyled
                      name='subTitle'
                      label='Sub Title'
                      rules={[
                        {
                          required: true,
                          message: 'Sub Title is required',
                        },
                        {
                          max: 50,
                          message: 'Max: 50 characters',
                        },
                      ]}
                    >
                      <Input size='large' placeholder='Please enter..' />
                    </FormItemNoMarginStyled>
                  </Col>
                  <Col xs={{ span: 24 }} md={{ span: 12 }} lg={{ span: 12 }} xl={{ span: 12 }}>
                    <FormItemNoMarginStyled name='description' label='Text Coupon'>
                      <TextArea placeholder='Please enter..' rows={3} showCount maxLength={255} />
                    </FormItemNoMarginStyled>
                  </Col>
                  <Col xs={{ span: 24 }} md={{ span: 12 }} lg={{ span: 12 }} xl={{ span: 12 }}>
                    <FormItemNoMarginStyled
                      name='promotionValue'
                      label='Promotion'
                      rules={[
                        {
                          required: true,
                          message: 'Promotion is required',
                        },
                      ]}
                    >
                      <Input placeholder='0' type='number' min={0} size='large' addonAfter={selectAfter} />
                    </FormItemNoMarginStyled>
                  </Col>
                </Row>
              </Box>
              <Box p={0.5} style={{ backgroundColor: 'rgba(246, 247, 252, 1)' }}>
                <Row>
                  <Col xs={{ span: 24 }} md={{ span: 8 }} lg={{ span: 8 }} xl={{ span: 8 }}>
                    <Box pl={0.5} pr={0.5}>
                      <BoxInfoFix>
                        <Text variant='H8' style={{ fontWeight: 'bold', borderRadius: '5px' }}>
                          Coupon Quantity
                        </Text>
                        <DividerStyled />
                        <Form.Item
                          name='couponQuantity'
                          rules={[
                            {
                              required: true,
                              message: 'Coupon Quantity is required',
                            },
                          ]}
                        >
                          <Input placeholder='0' min={0} type='number' size='large' />
                        </Form.Item>
                      </BoxInfoFix>
                    </Box>
                  </Col>
                  <Col xs={{ span: 24 }} md={{ span: 8 }} lg={{ span: 8 }} xl={{ span: 8 }}>
                    <Box pl={0.5} pr={0.5}>
                      <BoxInfoFix>
                        <Text variant='H8' style={{ fontWeight: 'bold' }}>
                          Start Date
                        </Text>
                        <DividerStyled />
                        <Form.Item
                          name='startDate'
                          rules={[
                            {
                              required: true,
                              message: 'Start Date is required',
                            },
                          ]}
                        >
                          <DatePicker
                            style={{ width: '100%' }}
                            size='large'
                            format='MM-DD-YYYY'
                            showTime={false}
                            inputReadOnly={true}
                          />
                        </Form.Item>
                      </BoxInfoFix>
                    </Box>
                  </Col>
                  <Col xs={{ span: 24 }} md={{ span: 8 }} lg={{ span: 8 }} xl={{ span: 8 }}>
                    <Box pl={0.5} pr={0.5}>
                      <BoxInfoFix>
                        <Text variant='H8' style={{ fontWeight: 'bold', borderRadius: '5px' }}>
                          Expires Date
                        </Text>
                        <DividerStyled />
                        <Form.Item
                          name='expiresDate'
                          rules={[
                            {
                              required: true,
                              message: 'Expires Date is required',
                            },
                          ]}
                        >
                          <SelectExpiresDate />
                        </Form.Item>
                      </BoxInfoFix>
                    </Box>
                  </Col>
                </Row>
              </Box>
            </Col>

            <Col xs={{ span: 24 }} md={{ span: 8 }} lg={{ span: 8 }} xl={{ span: 8 }}>
              <Box pl={1.5} display='flex' flexDirection='column' gap={1}>
                <Text variant='H7'>Template Image</Text>
                <Form.Item name='urlImage'>
                  <TemplateImageUpload options={lstCouponTemplate} />
                </Form.Item>
              </Box>
              {id ? (
                <Box
                  style={{ borderRadius: '5px', borderTopLeftRadius: '0px', textAlign: 'left', minHeight: '320px' }}
                  pl={1}
                  pr={1}
                  pt={1}
                >
                  <Box
                    className='d-between'
                    p={1}
                    style={{
                      background: 'rgba(255, 229, 231, 1)',
                      borderTopLeftRadius: '15px',
                      borderTopRightRadius: '15px',
                    }}
                  >
                    <Text variant='CONTENT_1'>
                      <span style={{ marginRight: '5px' }}>Coupon Quantity</span>
                    </Text>
                    <Text variant='CONTENT_1'>
                      <span style={{ marginRight: '5px' }}>{couponDetail?.quantity}</span>
                    </Text>
                  </Box>

                  <Box
                    className='d-between'
                    p={1}
                    style={{ background: 'rgba(255, 229, 231, 1)', marginTop: '1.5px', marginBottom: '1.5px' }}
                  >
                    <Text variant='CONTENT_1'>
                      <span style={{ marginRight: '5px' }}>Printed Quantity</span>
                    </Text>
                    <Text variant='CONTENT_1'>
                      <span style={{ marginRight: '5px' }}>{couponDetail?.totalPrint}</span>
                    </Text>
                  </Box>

                  <Box
                    className='d-between'
                    p={1}
                    style={{
                      background: 'rgba(255, 229, 231, 1)',
                      borderBottomLeftRadius: '15px',
                      borderBottomRightRadius: '15px',
                    }}
                  >
                    <Text variant='CONTENT_1'>
                      <span style={{ marginRight: '5px' }}>Coupon Used</span>
                    </Text>
                    <Text variant='CONTENT_1'>
                      <span style={{ marginRight: '5px' }}>{couponDetail?.couponUsed}</span>
                    </Text>
                  </Box>
                </Box>
              ) : null}
            </Col>
          </Row>
        </CouponsContentStyled>
      </Form>
    </PageStyled>
  );
};

export default CouponPage;

const CouponsContentStyled = styled.div`
  background: rgba(238, 238, 238, 1);
  margin-top: 1rem;
  padding: 1.5rem;

  && .ant-qrcode-borderless {
    margin-bottom: -8px;
    padding-bottom: 0 !important;
    padding-top: 0 !important;
  }
`;

const DividerStyled = styled(Divider)`
  border: 0.5px solid rgba(218, 218, 218, 1);
`;

const BoxInfoFix = styled(Box)`
  background: white;
  border-radius: 5px;
  padding: 10px;
  border: 1px solid rgba(218, 218, 218, 1);
`;

const BreadcrumbItemStyled = styled.div`
  font-size: 18px;
  font-weight: 500;
`;
